<template>
  <Popover class="inline-flex shrink-0">
    <Float
      auto-placement
      portal
      :arrow="5"
      :offset="10"
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <PopoverButton
        class="border-0 text-gray-400 ring-0 hover:text-gray-600"
        :title="title"
      >
        <FontAwesomeIcon :icon="['fal', 'cloud-slash']" />
      </PopoverButton>
      <PopoverPanel
        class="z-10 w-72 overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg"
      >
        <FloatArrow
          class="absolute h-5 w-5 rotate-45 transform border border-gray-300 bg-white"
        />
        <div class="relative flex flex-col gap-1.5 bg-white p-3 text-xs">
          <h3
            class="text-sm font-bold capitalize-first"
            :class="{
              'text-green-600': allChecksPass,
              'text-red-600': !allChecksPass
            }"
          >
            {{ title }}
          </h3>
          <div
            v-for="check in diagnostics"
            :key="check.code"
            class="flex items-start gap-1.5"
          >
            <FontAwesomeIcon
              :icon="['fas', checkIcon(check)]"
              class="mt-0.5"
              :class="{
                'text-green-500': check.ok,
                'text-gray-400': isUserChoiceCheck(check),
                'text-red-500': !check.ok && !isUserChoiceCheck(check)
              }"
            />
            <span class="flex flex-1 flex-col">
              <span class="text-gray-700 capitalize-first">
                {{ checkLabel(check) }}
              </span>
              <span
                v-if="check.attempt?.message"
                class="text-gray-500 capitalize-first"
              >
                {{ check.attempt.message }}
              </span>
            </span>
          </div>
        </div>
      </PopoverPanel>
    </Float>
  </Popover>
</template>

<script>
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Float, FloatArrow } from '@headlessui-float/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { paymentSyncPartnerName } from '@c/Partner/Chift/paymentSyncPartner'

export default {
  name: 'PaymentSyncDiagnosticsPopover',
  components: {
    Popover,
    PopoverButton,
    PopoverPanel,
    Float,
    FloatArrow,
    FontAwesomeIcon
  },
  props: {
    /**
     * Ordered checklist from the backend (PaymentSyncDiagnostics::diagnose()):
     * [{ code, ok, journal?, count?, partner?, enabled_at?, attempt? }, ...].
     * Kept generic here rather than
     * hardcoding the current codes, so a backend-side addition only needs
     * a new translation key, not a front change.
     */
    diagnostics: {
      type: Array,
      required: true
    }
  },
  computed: {
    allChecksPass() {
      return this.diagnostics.every((check) => check.ok)
    },
    /**
     * The same popover opens on a synchronized invoice (every check green) and
     * on a stalled one. "Why is the sync inactive?" over an all-green list read
     * as a contradiction, so the title states the outcome instead.
     */
    title() {
      return this.allChecksPass
        ? this.$t('invoicing.payments_sync_diagnostic_title_active')
        : this.$t('invoicing.payments_sync_diagnostic_title')
    }
  },
  methods: {
    /**
     * A payment encoded by hand interrupts the sync on purpose: it is the
     * user's choice, not a fault, so it gets a neutral marker rather than the
     * red cross of a broken prerequisite.
     */
    isUserChoiceCheck(check) {
      return (
        check.code === 'interruption_reason' &&
        check.reason === 'manual_payment_attached'
      )
    },
    checkIcon(check) {
      if (check.ok) {
        return 'circle-check'
      }

      if (this.isUserChoiceCheck(check)) {
        return 'circle-info'
      }

      return 'circle-xmark'
    },
    checkLabel(check) {
      if (check.code === 'company_connected') {
        return this.$t('invoicing.payments_sync_diagnostic_company_connected', {
          company: check.company ?? '—'
        })
      }

      if (check.code === 'diary_connected') {
        return this.$t('invoicing.payments_sync_diagnostic_diary_connected', {
          journal: check.journal ?? '—'
        })
      }

      // "aucun paiement manuel bloquant", crossed out with a red X, reads as a
      // double negative support has to untangle. Failing, this states the
      // problem directly instead - with the count, since one stray payment and
      // several are a different support case even though both read "blocked".
      if (check.code === 'no_blocking_payment' && !check.ok) {
        return this.$t('invoicing.payments_sync_diagnostic_blocking_payment', {
          count: check.count
        })
      }

      if (check.code === 'sent_to_partner') {
        return this.$t('invoicing.payments_sync_diagnostic_sent_to_partner', {
          partner: paymentSyncPartnerName(check.partner)
        })
      }

      if (check.code === 'established_after_opt_in') {
        return this.$t(
          'invoicing.payments_sync_diagnostic_established_after_opt_in',
          {
            enabled_at: check.enabled_at
              ? this.$h.formatDate(check.enabled_at, 'DD/MM/YYYY')
              : '—'
          }
        )
      }

      // Same double negative as the blocking payment: failing, state it.
      if (check.code === 'last_send_failure' && !check.ok) {
        return this.$t('invoicing.payments_sync_diagnostic_last_send_failed')
      }

      if (check.code === 'interruption_reason') {
        return this.$t(
          `invoicing.payments_sync_diagnostic_interruption_reason_${check.reason ?? 'unknown'}`
        )
      }

      return this.$t(`invoicing.payments_sync_diagnostic_${check.code}`)
    }
  }
}
</script>
